// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect, useRef } from 'react';
import { fetchWVStreams } from '../utils/streamService';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import MapView from '../components/MapView';
import { MapPin, Star, ChevronDown } from 'lucide-react';

const Map = () => {
  const [streams, setStreams] = useState([]);
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [selectedStreamId, setSelectedStreamId] = useState(null);
  const [streamDropdownOpen, setStreamDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const streamDropdownRef = useRef(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (streamDropdownRef.current && !streamDropdownRef.current.contains(e.target)) {
        setStreamDropdownOpen(false);
      }
    };
    if (streamDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [streamDropdownOpen]);

  useEffect(() => {
    loadStreams();
    loadFavorites();
  }, [currentUser]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredStreams(streams);
    } else {
      const filtered = streams.filter(stream =>
        stream.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        stream.county?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStreams(filtered);
    }
  }, [searchQuery, streams]);

  const loadStreams = async () => {
    try {
      const streamData = await fetchWVStreams();
      setStreams(streamData);
      setFilteredStreams(streamData);
    } catch (error) {
      console.error('Error loading streams:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    if (!currentUser) return;

    try {
      const q = query(
        collection(db, 'favorites'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const favIds = querySnapshot.docs.map(doc => doc.data().streamId);
      setFavorites(favIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const toggleFavorite = async (streamId) => {
    if (!currentUser) return;

    try {
      if (favorites.includes(streamId)) {
        // Remove favorite
        const q = query(
          collection(db, 'favorites'),
          where('userId', '==', currentUser.uid),
          where('streamId', '==', streamId)
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.docs.forEach(async (document) => {
          await deleteDoc(doc(db, 'favorites', document.id));
        });
        setFavorites(favorites.filter(id => id !== streamId));
      } else {
        // Add favorite
        await addDoc(collection(db, 'favorites'), {
          userId: currentUser.uid,
          streamId: streamId,
          streamName: streams.find(s => s.id === streamId)?.name,
          createdAt: new Date()
        });
        setFavorites([...favorites, streamId]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
        <h3>Loading streams from WV GIS...</h3>
      </div>
    );
  }

  return (
    <div className="map-layout">
      {/* Sidebar: desktop only (hidden on mobile) */}
      <div className="map-sidebar">
        <div className="map-search">
          <input
            type="text"
            className="search-input"
            placeholder="Search streams or counties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="stream-list">
          {filteredStreams.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem 1rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔍</div>
              <p style={{ fontSize: '0.9rem', color: '#6B7280' }}>No streams found</p>
            </div>
          ) : (
            filteredStreams.map(stream => (
              <div
                key={stream.id}
                className={`stream-list-item ${selectedStreamId === stream.id ? 'stream-list-item--selected' : ''}`}
                onClick={() => setSelectedStreamId(stream.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedStreamId(stream.id);
                  }
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div className="stream-name">{stream.name}</div>
                    {stream.county && (
                      <div className="stream-location">
                        <MapPin size={14} />
                        <span>{stream.county}</span>
                        {stream.mileage && <span> • {stream.mileage} mi</span>}
                      </div>
                    )}
                    {stream.nearCity && (
                      <div className="stream-location">
                        <span style={{ fontSize: '0.85rem' }}>Near {stream.nearCity}</span>
                      </div>
                    )}
                    {(stream.regType || stream.stockCode) && (
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {stream.regType && (
                          <span className={`badge ${stream.regType.includes('Catch') ? 'badge-catch-release' : 'badge-general'}`}>
                            {stream.regType}
                          </span>
                        )}
                        {stream.stockCode && stream.stockCode !== 'Unknown' && (
                          <span className="badge badge-general">
                            {stream.stockCode}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(stream.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      color: favorites.includes(stream.id) ? '#F59E0B' : '#D1D5DB'
                    }}
                  >
                    <Star size={20} fill={favorites.includes(stream.id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {filteredStreams.length > 0 && (
          <div className="stream-count">
            Showing {filteredStreams.length} of {streams.length} streams
          </div>
        )}
      </div>

      {/* Map */}
      <div className="map-container">
        <div className="map-search-bar">
          <input
            type="text"
            className="search-input"
            placeholder="Search streams or counties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search streams or counties"
          />
        </div>
        <div
          ref={streamDropdownRef}
          className={`map-stream-dropdown ${selectedStreamId ? 'select-has-value' : ''} ${streamDropdownOpen ? 'is-open' : ''}`}
          aria-label="Select a stream"
        >
          <button
            type="button"
            className="map-stream-dropdown-trigger"
            onClick={() => setStreamDropdownOpen(!streamDropdownOpen)}
            aria-expanded={streamDropdownOpen}
            aria-haspopup="listbox"
            aria-label={selectedStreamId ? `Selected: ${filteredStreams.find(s => s.id === selectedStreamId)?.name}` : 'Select a stream'}
          >
            <span className="map-stream-dropdown-label">
              {selectedStreamId
                ? (() => {
                    const s = filteredStreams.find(stream => stream.id === selectedStreamId);
                    return s ? `${s.name}${s.county ? ` (${s.county})` : ''}` : '';
                  })()
                : 'Select a stream...'}
            </span>
            <ChevronDown size={20} className="map-stream-dropdown-chevron" aria-hidden />
          </button>
          {streamDropdownOpen && (
            <div className="map-stream-dropdown-list" role="listbox">
              {filteredStreams.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔍</div>
                  <p style={{ fontSize: '0.9rem', color: '#6B7280' }}>No streams found</p>
                </div>
              ) : (
                filteredStreams.map(stream => (
                  <div
                    key={stream.id}
                    role="option"
                    aria-selected={selectedStreamId === stream.id}
                    className={`stream-list-item ${selectedStreamId === stream.id ? 'stream-list-item--selected' : ''}`}
                    onClick={() => {
                      setSelectedStreamId(stream.id);
                      setStreamDropdownOpen(false);
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div className="stream-name">{stream.name}</div>
                        {stream.county && (
                          <div className="stream-location">
                            <MapPin size={14} />
                            <span>{stream.county}</span>
                            {stream.mileage && <span> • {stream.mileage} mi</span>}
                          </div>
                        )}
                        {stream.nearCity && (
                          <div className="stream-location">
                            <span style={{ fontSize: '0.85rem' }}>Near {stream.nearCity}</span>
                          </div>
                        )}
                        {(stream.regType || stream.stockCode) && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {stream.regType && (
                              <span className={`badge ${stream.regType.includes('Catch') ? 'badge-catch-release' : 'badge-general'}`}>
                                {stream.regType}
                              </span>
                            )}
                            {stream.stockCode && stream.stockCode !== 'Unknown' && (
                              <span className="badge badge-general">{stream.stockCode}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(stream.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          color: favorites.includes(stream.id) ? '#F59E0B' : '#D1D5DB'
                        }}
                      >
                        <Star size={20} fill={favorites.includes(stream.id) ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className="map-area">
          <MapView
          streams={filteredStreams}
          favorites={favorites}
          selectedStreamId={selectedStreamId}
          onStreamDeselect={() => setSelectedStreamId(null)}
          onToggleFavorite={toggleFavorite}
        />
        </div>
      </div>
    </div>
  );
};

export default Map;
